import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  getQuotesSummary,
  type QuotesSummaryPosition,
} from '../../../shared/api/quotesSelection.api'
import type { MarkupConstants } from '../../../shared/api/exchangeRates.api'
import type { RequestItem, SourceType } from '../../../shared/api/types'
import { computeBestPriceForItem } from '../utils/computeBestPrice'
import type { SelectedPrice } from './useKpMode'

type Params = {
  requestId: string
  visibleItems: RequestItem[]
  exchangeRates: { USD: number; EUR: number; CNY: number }
  markupConstants: MarkupConstants
  setSelectedPrices: Dispatch<SetStateAction<Map<string, SelectedPrice>>>
}

/**
 * Подставляет в позиции квоты с ценой и валютой из сводки «Выбор квот».
 *
 * В карточке заявки квоты позиций приходят без цены — только поставщик и
 * матрица, а подписать строку КП верным поставщиком можно, лишь сверив цену
 * квоты с ценой в ячейке (№73). Позиций, которых нет в сводке (отменённые и
 * неактуальные), подстановка не касается.
 */
export function withQuotePrices(
  items: RequestItem[],
  positions: QuotesSummaryPosition[],
): RequestItem[] {
  const quotesByItem = new Map(positions.map((p) => [p.id, p.quotes]))
  return items.map((item) => {
    const quotes = quotesByItem.get(item.id)
    if (!quotes) return item
    return {
      ...item,
      // Порядок сводки тот же, что у квот в карточке заявки: от новых к старым.
      results: quotes.map((q) => ({
        id: q.id,
        supplierName: q.supplierName,
        sourceType: q.sourceType as SourceType,
        price: q.price ?? undefined,
        currency: q.currency,
        note: q.note,
      })),
    }
  })
}

/**
 * Кнопки «По лучшим ценам» и «Выбрать все позиции» в режиме КП: минимальная
 * цена в ₽ по каждой видимой позиции + поставщик той квоты, чья цена лежит в
 * выигравшей ячейке матрицы (№73).
 *
 * Цены квот подгружаются одним запросом сводки «Выбор квот». Не загрузилась —
 * подбор идёт по прежнему правилу (поставщик самой новой квоты матрицы): сами
 * цены от сводки не зависят, поэтому ошибку не показываем.
 */
export function useKpBestPrices({
  requestId,
  visibleItems,
  exchangeRates,
  markupConstants,
  setSelectedPrices,
}: Params) {
  const queryClient = useQueryClient()
  const [loading, setLoading] = useState(false)
  // Режим КП могли закрыть, пока грузилась сводка, — тогда выбор не трогаем.
  const aliveRef = useRef(true)
  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
    }
  }, [])

  // Видимые позиции с ценами квот из свежей сводки; сбой — позиции как есть.
  const loadPricedItems = async (): Promise<RequestItem[]> => {
    try {
      const summary = await queryClient.fetchQuery({
        queryKey: ['quotes-summary', requestId],
        queryFn: () => getQuotesSummary(requestId),
        staleTime: 0,
        // Без повторов: иначе при сбое сети кнопка «висела» бы на ретраях.
        retry: false,
      })
      return withQuotePrices(visibleItems, summary.positions)
    } catch {
      // Сводка не загрузилась — подбираем по прежнему правилу.
      return visibleItems
    }
  }

  const bestFor = (item: RequestItem) =>
    computeBestPriceForItem(item, exchangeRates.CNY, markupConstants, exchangeRates)

  const selectBest = async () => {
    setLoading(true)
    const items = await loadPricedItems()
    if (!aliveRef.current) return
    setLoading(false)

    // Перезаписывает текущий выбор целиком — менеджер может потом отдельные
    // позиции поменять вручную, кликнув по другой ячейке в таблице.
    const next = new Map<string, SelectedPrice>()
    for (const item of items) {
      const best = bestFor(item)
      if (best) next.set(item.id, best)
    }
    setSelectedPrices(next)
  }

  // Выбрать ВСЕ позиции (включая те, по которым нет ни одной цены) — они
  // попадут в КП с ценой 0. Позиции, у которых менеджер уже сделал выбор
  // вручную, не перезаписываются. Для остальных подбирается лучшая цена,
  // а если её нет — фиксируется заглушка cost=0 (matrix='china' условно,
  // чтобы UI показал колонку Китая; markup при cost=0 значения не имеет).
  const selectAll = async () => {
    setLoading(true)
    const items = await loadPricedItems()
    if (!aliveRef.current) return
    setLoading(false)

    setSelectedPrices((prev) => {
      const next = new Map(prev)
      for (const item of items) {
        if (next.has(item.id)) continue
        next.set(
          item.id,
          bestFor(item) ?? { col: 'price', matrix: 'china', cost: 0, supplierName: null },
        )
      }
      return next
    })
  }

  return { selectBest, selectAll, loading }
}
